/**
 * Hover Image Card Modal
 * Handles opening/closing modal with lightbox content and video playback.
 */

import { __ } from '@wordpress/i18n';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ReactPlayer from 'react-player';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

class HoverImageCardModal {
	constructor() {
		this.cards = document.querySelectorAll('.block-hover-image-card');
		this.modal = null;
		this.modalContent = null;
		this.videoRoot = null;

		if (this.cards.length > 0) {
			this.init();
		}
	}

	init() {
		this.cards.forEach((card) => {
			const trigger = card.querySelector(
				'.block-hover-image-card__trigger'
			);
			if (trigger) {
				trigger.addEventListener('click', (e) => {
					this.openModal(e, trigger);
				});
			}
		});
	}

	createModal() {
		this.modal = document.createElement('div');
		this.modal.classList.add('hover-image-card-modal');
		this.modal.setAttribute('role', 'dialog');
		this.modal.setAttribute('aria-modal', 'true');

		const container = document.createElement('div');
		container.classList.add('hover-image-card-modal__container');

		const closeBtn = document.createElement('button');
		closeBtn.classList.add('hover-image-card-modal__close');
		closeBtn.setAttribute('aria-label', __('Close modal', 'propel'));
		closeBtn.addEventListener('click', (e) => this.closeModal(e));

		this.modalContent = document.createElement('div');
		this.modalContent.classList.add('hover-image-card-modal__content');

		container.appendChild(closeBtn);
		container.appendChild(this.modalContent);
		this.modal.appendChild(container);

		// Close on backdrop click.
		this.modal.addEventListener('click', (e) => {
			if (e.target === this.modal) {
				this.closeModal(e);
			}
		});

		document.body.appendChild(this.modal);
	}

	openModal(e, trigger) {
		e.preventDefault();

		if (!this.modal) {
			this.createModal();
		}

		const data = JSON.parse(
			trigger.getAttribute('data-modal-data') || '{}'
		);
		this.renderModalContent(data);

		this.modal.classList.add('active');
		const container = this.modal.querySelector(
			'.hover-image-card-modal__container'
		);
		disableBodyScroll(container, { reserveScrollBarGap: true });

		setTimeout(() => {
			this.modal.classList.add('visible');
		}, 10);

		document.addEventListener('keydown', this.handleKeydown);
	}

	renderModalContent(data) {
		let html = '';

		// Heading.
		if (data.lightbox_heading) {
			html += `<h2 class="hover-image-card-modal__title">${this.escapeHtml(data.lightbox_heading)}</h2>`;
		}

		// Description.
		if (data.lightbox_description) {
			html += `<p class="hover-image-card-modal__description">${this.escapeHtml(data.lightbox_description)}</p>`;
		}

		// Button.
		if (data.lightbox_button && data.lightbox_button.url) {
			const target = data.lightbox_button.target || '_self';
			html += `<div class="hover-image-card-modal__btn">
				<div class="wp-block-button is-style-secondary wp-block-button--icon-right" style="--buttonIcon: var(--icon-arrow-right);">
					<a class="wp-block-button__link" href="${this.escapeHtml(data.lightbox_button.url)}" target="${target}">${this.escapeHtml(data.lightbox_button.title || __('Learn More', 'propel'))}</a>
				</div>
			</div>`;
		}

		// Video section.
		if (data.video_url && data.video_thumbnail) {
			html += `<div class="hover-image-card-modal__video">
				<div class="hover-image-card-modal__video-wrapper component-video bg-dark" data-embed-url="${this.escapeHtml(data.video_url)}">
					<img src="${this.escapeHtml(data.video_thumbnail)}" alt="" class="hover-image-card-modal__video-thumbnail" />
					<button class="c-btn--play hover-image-card-modal__video-play" type="button">
						<span class="sr-only">${__('Play video', 'propel')}</span>
					</button>
				</div>
			</div>`;
		}

		// Testimonial.
		if (data.testimonial_heading) {
			html += `<h4 class="hover-image-card-modal__testimonial-heading">${this.escapeHtml(data.testimonial_heading)}</h4>`;
		}

		if (data.testimonial_content) {
			html += `<blockquote class="hover-image-card-modal__testimonial">${this.escapeHtml(data.testimonial_content)}</blockquote>`;
		}

		this.modalContent.innerHTML = html;

		// Initialize video player.
		const videoWrapper =
			this.modalContent.querySelector('.component-video');
		if (videoWrapper) {
			const playBtn = videoWrapper.querySelector('.c-btn--play');
			if (playBtn) {
				playBtn.addEventListener('click', () =>
					this.playVideo(videoWrapper)
				);
			}
			videoWrapper.addEventListener('click', () =>
				this.playVideo(videoWrapper)
			);
		}
	}

	playVideo(videoWrapper) {
		if (!videoWrapper || videoWrapper.classList.contains('playing')) {
			return;
		}

		const videoUrl = videoWrapper.getAttribute('data-embed-url');
		if (!videoUrl) {
			return;
		}

		videoWrapper.classList.add('playing');

		// Clear existing content.
		videoWrapper.innerHTML = '';

		// Create React root and render player.
		this.videoRoot = createRoot(videoWrapper);
		this.videoRoot.render(
			React.createElement(ReactPlayer, {
				url: videoUrl,
				playing: true,
				width: '100%',
				height: '100%',
				controls: true,
				style: { position: 'absolute', top: 0, left: 0 },
			})
		);
	}

	closeModal = (e) => {
		if (e) {
			e.preventDefault();
		}

		this.modal.classList.remove('visible');

		// Stop video.
		if (this.videoRoot) {
			this.videoRoot.unmount();
			this.videoRoot = null;
		}

		setTimeout(() => {
			this.modal.classList.remove('active');
			clearAllBodyScrollLocks();
			this.modalContent.innerHTML = '';
		}, 300);

		document.removeEventListener('keydown', this.handleKeydown);
	};

	handleKeydown = (e) => {
		if (e.key === 'Escape') {
			this.closeModal(e);
		}
	};

	escapeHtml(text) {
		if (!text) return '';
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}
}

// Initialize when DOM is ready.
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		new HoverImageCardModal();
	});
} else {
	new HoverImageCardModal();
}
